import { memo, useState, useMemo, useCallback, useId } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Typography,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import DeploymentRowOverview from './DeploymentRowOverview';
import DeploymentRowParameters from './DeploymentRowParameters';
import DeploymentRowHistory from './DeploymentRowHistory';
import TerminateConfirm from './TerminateConfirm';
import UnsubscribeConfirm from './UnsubscribeConfirm';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import { useDeploymentRowActions } from '../hooks/useDeploymentRowActions';
import { rowConfigFor, type RowConfig } from '../rowConfig';
import type { Deployment } from '../../../types/backend';
import type { ServiceComponentSummary } from '../services/catalogService';
import type { DependencyInfo } from '../hooks/useServiceComponentDependencies';

interface DeploymentRowProps {
  customerId: string;
  deployment: Deployment;
  product: ServiceComponentSummary | undefined;
  dependencies: DependencyInfo[] | undefined;
  recentVersions: string[];
  onRefresh: () => void;
}

const SEVERITY_BORDER_COLOR: Record<RowConfig['severity'], string> = {
  err: 'error.main',
  warn: 'warning.main',
  info: 'info.main',
  ok: 'success.main',
  muted: 'divider',
};

const PILL_COLOR: Record<RowConfig['severity'], 'error' | 'warning' | 'info' | 'success' | 'default'> = {
  err: 'error',
  warn: 'warning',
  info: 'info',
  ok: 'success',
  muted: 'default',
};

const DeploymentRow = memo<DeploymentRowProps>(({
  customerId,
  deployment,
  product,
  dependencies,
  recentVersions,
  onRefresh,
}) => {
  const config = useMemo(
    () => rowConfigFor(deployment, product, dependencies),
    [deployment, product, dependencies],
  );

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<RowConfig['defaultTab']>(config.defaultTab);
  const [kebabAnchor, setKebabAnchor] = useState<HTMLElement | null>(null);
  const [showTerminate, setShowTerminate] = useState(false);
  const [showUnsubscribe, setShowUnsubscribe] = useState(false);

  const actions = useDeploymentRowActions({
    customerId,
    deployment,
    onRefresh,
  });

  const uid = useId();
  const tabId = (t: string) => `${uid}-tab-${t}`;
  const panelId = (t: string) => `${uid}-panel-${t}`;

  const handleToggle = useCallback(() => setOpen((v) => !v), []);
  const closeKebab = useCallback(() => setKebabAnchor(null), []);

  const stackName =
    deployment.actual_stack_id?.split(':').pop()?.split('/')[1] ??
    deployment.product_name ??
    deployment.product_id;
  const confirmToken = deployment.product_name || deployment.product_id;

  return (
    <>
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderLeft: 3,
          borderLeftColor: SEVERITY_BORDER_COLOR[config.severity],
          borderRadius: 1,
          mb: 0.75,
          bgcolor: 'background.paper',
          transition: 'box-shadow .15s',
          '&:hover': { boxShadow: 1 },
          boxShadow: open ? 2 : 0,
        }}
      >
        <Box
          role="button"
          tabIndex={0}
          onClick={handleToggle}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle(); } }}
          aria-expanded={open}
          aria-label={`${config.identity.product} deployment, ${config.pill.label}`}
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto auto',
            alignItems: 'center',
            gap: 2,
            p: 1.5,
            cursor: 'pointer',
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {config.identity.product}
              </Typography>
              {config.identity.productDeprecated && (
                <Chip
                  label="Deprecated"
                  color="warning"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: 10, height: 18 }}
                />
              )}
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', gap: 1 }}>
              <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 11.5 }}>
                {config.identity.account}
              </Box>
              <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
              <Box component="span">{config.identity.region}</Box>
              {config.identity.version && (
                <>
                  <Box component="span" sx={{ color: 'text.disabled' }}>·</Box>
                  <Box component="span">{config.identity.version}</Box>
                </>
              )}
            </Typography>
          </Box>
          <Chip
            label={`● ${config.pill.label}`}
            color={PILL_COLOR[config.pill.severity]}
            variant={config.pill.severity === 'muted' ? 'outlined' : 'filled'}
            size="small"
            sx={{ fontWeight: 500 }}
          />
          <IconButton
            size="small"
            tabIndex={-1}
            aria-hidden="true"
            sx={{ transform: open ? 'rotate(180deg)' : undefined, transition: 'transform .2s', color: 'text.disabled' }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </Box>

        {open && (
          <Box sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'action.hover', p: 1.5 }}>
            {config.metaStrip.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  fontSize: 12,
                  color: 'text.secondary',
                  pb: 1,
                  mb: 1.5,
                  borderBottom: '1px dashed',
                  borderColor: 'divider',
                }}
              >
                {config.metaStrip.map((t, i) =>
                  'who' in t ? (
                    <Box component="span" key={i} sx={{ fontFamily: 'monospace', color: 'text.primary' }}>
                      {t.who}
                    </Box>
                  ) : (
                    <Box component="span" key={i}>{t.text}</Box>
                  ),
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ minHeight: 36 }} aria-label="Deployment details">
                <Tab value="overview" label="Overview" id={tabId('overview')} aria-controls={panelId('overview')} sx={{ minHeight: 36, textTransform: 'none' }} />
                <Tab value="params" label="Parameters" id={tabId('params')} aria-controls={panelId('params')} sx={{ minHeight: 36, textTransform: 'none' }} />
                <Tab value="history" label="History" id={tabId('history')} aria-controls={panelId('history')} sx={{ minHeight: 36, textTransform: 'none' }} />
              </Tabs>
              <IconButton
                size="small"
                onClick={(e) => setKebabAnchor(e.currentTarget)}
                aria-label="More actions"
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
            <Divider />

            <Box role="tabpanel" id={panelId('overview')} aria-labelledby={tabId('overview')} hidden={tab !== 'overview'} sx={{ py: 1 }}>
              {tab === 'overview' && (
                <DeploymentRowOverview
                  customerId={customerId}
                  deployment={deployment}
                  state={config.state}
                  onSwitchToParameters={() => setTab('params')}
                  actions={actions}
                  recentVersions={recentVersions}
                />
              )}
            </Box>
            <Box role="tabpanel" id={panelId('params')} aria-labelledby={tabId('params')} hidden={tab !== 'params'} sx={{ py: 1 }}>
              {tab === 'params' && (
                <DeploymentRowParameters
                  customerId={customerId}
                  deployment={deployment}
                  onRefresh={onRefresh}
                />
              )}
            </Box>
            <Box role="tabpanel" id={panelId('history')} aria-labelledby={tabId('history')} hidden={tab !== 'history'} sx={{ py: 1 }}>
              {tab === 'history' && (
                <DeploymentRowHistory
                  customerId={customerId}
                  deploymentId={deployment.id}
                  active={tab === 'history'}
                />
              )}
            </Box>

            {showTerminate && (
              <TerminateConfirm
                stackName={stackName}
                accountId={deployment.account_id}
                confirmToken={confirmToken}
                submitting={actions.submitting}
                onCancel={() => setShowTerminate(false)}
                onConfirm={() => {
                  actions.terminate();
                  setShowTerminate(false);
                }}
              />
            )}

            {showUnsubscribe && (
              <UnsubscribeConfirm
                service={deployment.service}
                accountId={deployment.account_id}
                region={deployment.region}
                confirmToken={deployment.service}
                submitting={actions.submitting}
                onCancel={() => setShowUnsubscribe(false)}
                onConfirm={() => {
                  actions.unsubscribe();
                  setShowUnsubscribe(false);
                }}
              />
            )}
          </Box>
        )}
      </Box>

      <Menu
        anchorEl={kebabAnchor}
        open={Boolean(kebabAnchor)}
        onClose={closeKebab}
        onClick={closeKebab}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={actions.requestHeartbeat} disabled={actions.submitting}>
          Request heartbeat
        </MenuItem>
        <MenuItem onClick={actions.triggerReconciler} disabled={actions.submitting}>
          Trigger reconciler now
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => setShowUnsubscribe(true)}
          disabled={actions.submitting || config.state === 'terminating' || config.state === 'deregistering' || config.state === 'unsubscribed'}
          sx={{ color: 'warning.main' }}
        >
          Unsubscribe…
        </MenuItem>
        <MenuItem
          onClick={() => setShowTerminate(true)}
          disabled={actions.submitting || config.state === 'terminating' || config.state === 'deregistering' || config.state === 'unsubscribed'}
          sx={{ color: 'error.main' }}
        >
          Terminate deployment…
        </MenuItem>
      </Menu>

      <SnackbarAlert snackbar={actions.snackbar} onClose={actions.closeSnackbar} />
    </>
  );
});

DeploymentRow.displayName = 'DeploymentRow';
export default DeploymentRow;
